/* eslint-disable max-lines */
/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Components from '../..';
import Hooks from '../../../hooks';
import {
  BasketItemType,
  IAttractionsBasketItem,
  IBasketItem,
} from '../../../modules/basket/models';
import {
  IOption,
  IProduct,
  IProductAdditionalSettings,
  IProductComponents,
} from '../../../modules/common/models/productModels';
import './Attractions.scss';
import { addToBasket } from '../../../modules/basket/operations';
import { IAttractions } from '../../../modules/attractions/models';
import Icons from '../../../assets/icons';
import InformationPopup from '../../InformationPopup/InformationPopup';
import getClientsStyle from '../../../utils/getClientsStyle';
import { getAttractionsDate } from '../../../modules/attractions/selectors';
import moment from 'moment';
import DatePickerDropdown from '../../DatePickerDropdown/DatePickerDropdown';
import { normalizeLimit, inBasketQtyForProduct } from '../../../utils/limits';
import {
  isCategoryStandaloneEligible,
  cartHasEligibleForSameProduct,
} from '../../../utils/standalone';
interface IProps {
  attraction: IAttractions;
}

const Attractions = (props: IProps) => {
  const {
    attraction: {
      name,
      description,
      defaultPrice,
      consumerCategories,
      coverImage,
      // expirationDate,
      icon,
      productComponents,
      availabilityCount,
      unlimited,
    },
  } = props;

  const { t } = useTranslation(['components', 'common']);
  const styleForClient = getClientsStyle();
  const [isVisibleInfoPopup, setIsVisibleInfoPopup] = useState(false);
  const [isVisibleCategoryPopup, setIsVisibleCategoryPopup] = useState(false);
  const [idCategory, setIdCategory] = useState('');
  const [showSmallPopup, setShowSmallPopup] = useState(false);
  const [hours, setHours] = useState<IProductComponents[]>();
  const [quantities, setQuantities] = useState<{ [key: string]: number }>({});
  const quantitiesSum = Object.values(quantities).reduce(
    (acc, curr) => acc + curr,
    0
  );
  const [suppressValidationTooltip, setSuppressValidationTooltip] =
    useState(false);
  const suppressTimerRef = useRef<
    number | ReturnType<typeof setTimeout> | null
  >(null);

  const currentCurrency = consumerCategories[0]?.price?.currency;
  // const dateFormat = Hooks.useDateFormat();
  const { getPriceDisplay } = Hooks.usePriceDisplay(currentCurrency);

  const dateTop = Hooks.useAppSelector((s) =>
    getAttractionsDate(s.attractions)
  );
  const minDateISO = dateTop
    ? moment(dateTop).format('YYYY-MM-DD')
    : moment().format('YYYY-MM-DD');
  const maxDateISO = moment(minDateISO).add(7, 'days').format('YYYY-MM-DD');

  const basketItems = Hooks.useAppSelector((s) => s.basket.items);

  const limit = normalizeLimit(
    (props.attraction as any)?.settings?.maximumQuantity
  );

  const inBasket = inBasketQtyForProduct(basketItems, props.attraction);

  const remainingBase =
    limit == null ? Infinity : Math.max(0, limit - inBasket);

  const remainingLive =
    limit == null ? Infinity : Math.max(0, remainingBase - quantitiesSum);

  const hasAnySelection = quantitiesSum > 0;

  const selectionHasEligible = Object.entries(quantities).some(([catId, q]) => {
    if (!q || q <= 0) return false;
    const cat = consumerCategories.find((c) => c.id === catId);
    return isCategoryStandaloneEligible(cat as any);
  });

  const cartHasEligibleSameProduct = cartHasEligibleForSameProduct(
    basketItems,
    props.attraction
  );

  const standaloneViolation =
    hasAnySelection && !selectionHasEligible && !cartHasEligibleSameProduct;

  useEffect(() => {
    const processedProductComponents = productComponents.map((product) => ({
      ...product,
      productAdditionalSettings: product.productAdditionalSettings.map(
        // eslint-disable-next-line max-nested-callbacks
        (setting) => ({
          ...setting,
          value: '',
        })
      ),
    }));
    setHours(processedProductComponents);
  }, [productComponents]);

  useEffect(
    () => () => {
      if (suppressTimerRef.current)
        clearTimeout(suppressTimerRef.current as any);
    },
    []
  );

  const totalPrice = () => {
    let total = 0;

    for (const categoryId in quantities) {
      const quantity = quantities[categoryId];
      const pickedCategory = consumerCategories.find(
        (cat) => cat.id === categoryId
      );
      if (pickedCategory) {
        total += +(pickedCategory?.price?.grossAmount || 0) * quantity;
      }
    }
    return total;
  };

  const dispatch = Hooks.useAppDispatch();
  const date = Hooks.useAppSelector((s) => getAttractionsDate(s.attractions));

  const handleAddToBasket = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (quantitiesSum <= 0) return;

    if (standaloneViolation) return;

    let left = remainingBase;
    const itemsToAdd: IBasketItem[] = [];

    for (const category of consumerCategories) {
      const categoryId = category.id;
      const wanted = quantities[categoryId] || 0;
      if (wanted <= 0) continue;

      const allowed =
        limit == null ? wanted : Math.min(wanted, Math.max(0, left));
      if (allowed <= 0) continue;

      itemsToAdd.push({
        itemType: BasketItemType.Attractions,
        price: category.price?.grossAmount,
        quantity: allowed,
        date,
        productComponents: hours,
        currency: category.price?.currency,
        item: props.attraction as IProduct,
        consumerCategoryId: categoryId,
        pickupMethod: 'onSite',
        priceBeforeRecalculation: 0,
        priceRecalculated: false,
        name,
      } as IAttractionsBasketItem);

      if (limit != null) left -= allowed;
    }

    if (itemsToAdd.length === 0) return;

    dispatch(addToBasket(itemsToAdd));
    e.currentTarget.reset();
    setQuantities({});

    setSuppressValidationTooltip(true);
    if (suppressTimerRef.current) clearTimeout(suppressTimerRef.current as any);
    suppressTimerRef.current = setTimeout(() => {
      setSuppressValidationTooltip(false);
    }, 4000);

    setShowSmallPopup(true);
  };

  const handleQuantityChange = (categoryId: string, nextQty: number) => {
    if (
      !unlimited &&
      availabilityCount &&
      nextQty > (quantities[categoryId] || 0) &&
      quantitiesSum + 1 >= availabilityCount
    ) {
      return;
    }

    const othersSum = Object.entries(quantities).reduce((acc, [id, q]) => {
      if (id === categoryId) return acc;
      return acc + (q || 0);
    }, 0);

    const allowedForThis =
      limit == null ? nextQty : Math.max(0, remainingBase - othersSum);

    const clamped = limit == null ? nextQty : Math.min(nextQty, allowedForThis);

    setQuantities((prev) => ({
      ...prev,
      [categoryId]: Math.max(0, clamped),
    }));
  };

  const minPriceBeforeRecalculation = Number(
    consumerCategories?.find((typ) => typ.name == 'normalny')
      ?.priceBeforeRecalculation?.grossAmount
  );

  const conversionOfDiscountIntoCash = Math.round(
    Number(minPriceBeforeRecalculation) - Number(defaultPrice)
  );

  const handleToggleInfoPopup = () => {
    setIsVisibleInfoPopup(!isVisibleInfoPopup);
  };

  const handleToggleCategoryPopup = () => {
    setIsVisibleCategoryPopup(!isVisibleCategoryPopup);
  };

  const handleCategoryInfoClick = (categoryId: string) => {
    setIdCategory(categoryId);
    handleToggleCategoryPopup();
  };

  const handleCloseInfoPopup = () => {
    setIsVisibleInfoPopup(false);
  };

  const handleCloseCategoryPopup = () => {
    setIsVisibleCategoryPopup(false);
  };

  const productDropdowns = productComponents.map(
    (product: IProductComponents) => {
      const dropdownOptions = product.productAdditionalSettings[0]?.options.map(
        (option: IOption) => ({
          label: option.name,
          value: option.name,
        })
      );

      return {
        productId: product.id,
        dropdownOptions: dropdownOptions,
      };
    }
  );

  const handleAdditionalSettingChange = (
    productId: number,
    settingKey: string | number,
    value: string
  ) => {
    const updated = hours?.map((product) => {
      if (product.id !== productId) return { ...product };
      const productAdditionalSettings = product.productAdditionalSettings.map(
        (s) =>
          String(s.id ?? s.name) === String(settingKey)
            ? { ...s, value }
            : { ...s }
      );
      return { ...product, productAdditionalSettings };
    });
    setHours(updated);
  };

  const handleCategoryChange = (
    value: string,
    productId: number,
    settingName: string
  ) => {
    const updated = hours?.map((product) => {
      if (product.id !== productId) return { ...product };
      const productAdditionalSettings = product.productAdditionalSettings.map(
        (s) => (product.name === settingName ? { ...s, value } : { ...s })
      );
      return { ...product, productAdditionalSettings };
    });
    setHours(updated);
  };

  const getDiscountPercentage = (count: number) => {
    switch (count) {
      case 2:
        return 10;
      case 3:
        return 15;
      case 4:
        return 20;
      default:
        return 0;
    }
  };

  const discountPercentage = getDiscountPercentage(productComponents.length);

  const hasAdditional =
    Array.isArray(hours) &&
    hours.some(
      (c: IProductComponents) => (c.productAdditionalSettings?.length ?? 0) > 0
    );

  const additionalComplete =
    Array.isArray(hours) &&
    hours.every((c: IProductComponents) =>
      (c.productAdditionalSettings ?? []).every(
        (p: IProductAdditionalSettings) =>
          p && p.value !== undefined && p.value !== null
            ? String(p.value).trim() !== ''
            : true
      )
    );

  const qtyOk =
    quantitiesSum > 0 &&
    ((availabilityCount &&
      !unlimited &&
      quantitiesSum < availabilityCount + 1) ||
      (unlimited === true && availabilityCount === 0));

  const tooltipKey =
    !qtyOk && hasAdditional && !additionalComplete
      ? ('components:attractions.validator_for_submit_additional' as const)
      : qtyOk && hasAdditional && !additionalComplete
      ? ('components:attractions.validator_for_submit_only_additional' as const)
      : !qtyOk
      ? ('components:attractions.validator_for_submit' as const)
      : ('components:attractions.validator_for_submit' as const);

  const qtyOkWithLimit = qtyOk && (limit == null || remainingBase > 0);

  const canSubmit =
    qtyOkWithLimit &&
    (!hasAdditional || additionalComplete) &&
    !standaloneViolation;

  return (
    <>
      {isVisibleInfoPopup && (
        <InformationPopup
          attractionName={props.attraction.name}
          attractionDescription={props.attraction.description}
          attractionFullDescription={props.attraction?.fullDescription}
          isVisibleInfoPopup={isVisibleInfoPopup}
          handleClose={handleCloseInfoPopup}
        />
      )}
      {isVisibleCategoryPopup && (
        <InformationPopup
          attractionCategoryDescription={
            props.attraction?.consumerCategories.find(
              (x) => x.id === idCategory
            )?.description
          }
          attractionNameCategory={
            props.attraction?.consumerCategories.find(
              (x) => x.id === idCategory
            )?.name
          }
          isVisibleInfoPopup={isVisibleCategoryPopup}
          handleClose={handleCloseCategoryPopup}
        />
      )}
      {unlimited === true ||
      (unlimited === false && availabilityCount && availabilityCount > 0) ? (
        <form
          onSubmit={handleAddToBasket}
          className="berg-components-attractions-wrapper"
        >
          <div className="berg-components-attractions__picture flex flex-col justify-between">
            <img src={coverImage?.url} alt={name} loading="lazy" />

            <div className="flex flex-col gap-4 bg-white rounded-2xl sm:pb-8">
              {/* <div
                className="berg-components-attractions__box"
                data-client-style={styleForClient}
              >
                {t('attractions.box')}
              </div> */}
              <div className="berg-components-attractions__title flex flex-row gap-1 justify-center items-center">
                <div className="name">{name}</div>
                <button onClick={handleToggleInfoPopup} type="button">
                  <Icons.InfoBrama />
                </button>
              </div>
              <div
                className="berg-components-attractions__sum"
                aria-disabled={!quantities}
              >
                <div className="berg-components-attractions__sum--second">
                  <p>
                    {t('components:attractions.from')}{' '}
                    {getPriceDisplay(defaultPrice || 0)}
                  </p>
                  {consumerCategories?.find((typ) => typ.name == 'normalny')
                    ?.priceRecalculated === true && (
                    <div>
                      {t('components:attractions.cheap')}{' '}
                      {getPriceDisplay(conversionOfDiscountIntoCash)}
                    </div>
                  )}
                </div>
                {consumerCategories?.find((typ) => typ.name == 'normalny')
                  ?.priceRecalculated === true && (
                  <div className="berg-components-attractions__sum--first">
                    {t('components:attractions.price_before_recalculated')}{' '}
                    <p>{getPriceDisplay(minPriceBeforeRecalculation || 0)}</p>
                  </div>
                )}

                <div className="berg-components-attractions__sum--third">
                  {t('components:attractions.price_previous_days')}{' '}
                  {getPriceDisplay(defaultPrice || 0)}
                </div>
                {unlimited === false && (
                  <div className="berg-components-attractions__sum--first text-[14px] mt-[5px]">
                    {t('components:attractions.number_of_available_packages')}:{' '}
                    {availabilityCount}
                  </div>
                )}
                {unlimited === true && (
                  <div className="berg-components-attractions__sum--first text-[14px] mt-[5px]"></div>
                )}
              </div>
            </div>
          </div>
          <div className="berg-components-attractions__content">
            <div className="flex flex-row gap-8 absolute top-[-1.5rem] sm:top-[-2rem]">
              <div
                className={`bg-white rounded-lg w-full p-1 sm:p-2 text-black border-solid border-[#b0cb1f] border-2 text-[14px] sm:text-[20px] font-semibold text-nowrap`}
              >
                {productComponents?.length}{' '}
                {t('components:attractions.attractions')}
              </div>
              {discountPercentage > 0 && (
                <div
                  className={`bg-[#b0cb1f] rounded-lg w-full p-1 sm:p-2 text- text-[14px] sm:text-[20px] font-semibold text-nowrap`}
                >
                  {t('components:attractions.saving')} -{discountPercentage}%
                </div>
              )}
            </div>
            <div className="berg-components-attractions__choices">
              {consumerCategories.map((category) => (
                <div
                  key={category.id}
                  className="flex flex-row items-center justify-between rectangle"
                >
                  <div className="flex flex-row items-center gap-4">
                    <span>{category.name}</span>
                    {getPriceDisplay(
                      category?.price?.grossAmount !== undefined
                        ? Number(category.price.grossAmount).toFixed(2)
                        : '0.00'
                    )}
                  </div>
                  <div className="flex flex-row gap-2">
                    <button
                      onClick={() => handleCategoryInfoClick(category.id)}
                      type="button"
                    >
                      {category.description && <Icons.InfoBrama />}
                    </button>
                    <Components.NumberCounter
                      counter={quantities[category?.id] || 0}
                      onChange={(count: number) =>
                        handleQuantityChange(category?.id, count)
                      }
                      maxCounter={99}
                    />
                    <div className="flex flex-col gap-2">
                      {/* badge „Pozostało” */}
                      {limit != null && (
                        <span
                          className={`attractions-remaining-badge ${
                            remainingLive === 0
                              ? 'danger'
                              : remainingLive <= 3
                              ? 'warn'
                              : 'ok'
                          }`}
                          aria-live="polite"
                          title={t('common:remaining')}
                        >
                          {t('common:remaining')}:{' '}
                          {Number.isFinite(remainingLive) ? remainingLive : '∞'}
                        </span>
                      )}
                      {/* progress do limitu */}
                      {limit != null && (
                        <div
                          className="attractions-remaining-progress"
                          role="progressbar"
                          aria-valuemin={0}
                          aria-valuemax={limit}
                          aria-valuenow={Math.min(limit, limit - remainingLive)}
                          title={`${t('common:selected')}: ${
                            limit - remainingLive
                          } / ${limit}`}
                        >
                          <div
                            className="bar"
                            style={{
                              width: `${
                                ((limit - remainingLive) / Math.max(1, limit)) *
                                100
                              }%`,
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {productComponents &&
                productComponents.map(
                  (product: IProductComponents, productIndex: number) =>
                    product.productAdditionalSettings.length > 0 ? (
                      <div
                        key={product.id}
                        className="flex flex-col sm:flex-row items-center w-full gap-8"
                        style={{
                          zIndex: productComponents?.length - productIndex,
                        }}
                      >
                        <div className="text-start">
                          {product.productAdditionalSettings.map((setting) => (
                            <div
                              key={String(setting.id ?? setting.name)}
                              className="mb-3"
                            >
                              <span className="block mb-1">{setting.name}</span>

                              {setting.inputType === 'date' ? (
                                <div className="berg-components-date-dropdown">
                                  <DatePickerDropdown
                                    name={`AttractionsDate_${productIndex}_${
                                      setting.id ?? setting.name
                                    }`}
                                    value={setting.value ?? null}
                                    onChange={(isoYmd) =>
                                      handleAdditionalSettingChange(
                                        product.id,
                                        setting.id ?? setting.name,
                                        isoYmd
                                      )
                                    }
                                    minDate={minDateISO}
                                    maxDate={maxDateISO}
                                    placeholder={
                                      t(
                                        'components:attractions.choose'
                                      ) as string
                                    }
                                  />
                                </div>
                              ) : (
                                <Components.Dropdown
                                  options={
                                    productDropdowns.find(
                                      (d) => d.productId === product.id
                                    )?.dropdownOptions || []
                                  }
                                  name={`AttractionsTime_${productIndex}_${
                                    setting.id ?? setting.name
                                  }`}
                                  onChange={(val: string) =>
                                    handleAdditionalSettingChange(
                                      product.id,
                                      setting.id ?? setting.name,
                                      val
                                    )
                                  }
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null
                )}
            </div>
            <div
              className="berg-components-attractions__submit flex flex-col  gap-4 justify-center"
              data-client-style={styleForClient}
            >
              {/* komunikat o regule standalone */}
              {standaloneViolation && (
                <div
                  className="attractions-inline-alert"
                  role="alert"
                  aria-live="polite"
                >
                  {t('common:standalone_required')}
                </div>
              )}
              <div className="flex flex-row justify-center gap-2 lg:items-center">
                <strong>{t('components:attractions.total')}</strong>{' '}
                <p>{getPriceDisplay(totalPrice())}</p>
              </div>
              {canSubmit ? (
                <button>
                  {t('components:attractions.submit', { count: quantitiesSum })}
                </button>
              ) : suppressValidationTooltip ? (
                <button disabled>
                  {t('components:attractions.submit_zero')}
                </button>
              ) : (
                <Components.Tooltip content={t(tooltipKey)}>
                  <button disabled>
                    {t('components:attractions.submit_zero')}
                  </button>
                </Components.Tooltip>
              )}
            </div>

            <div className="berg-components-attractions__info">
              <div className="flex flex-col items-start gap-4 mt-4 sm:mt-8 min-w-0 berg-components-attractions__hour">
                {productComponents &&
                  productComponents.map(
                    (product: IProductComponents, productIndex: number) => (
                      <div
                        key={product.id}
                        className="flex flex-col sm:flex-row items-start self-start w-auto min-w-0 gap-8"
                        style={{
                          zIndex: productComponents?.length - productIndex,
                        }}
                      >
                        <div className="flex flex-col gap-1 items-start ">
                          <div className="self-start">
                            {product.attractionName}
                          </div>

                          <span>{product.description}</span>
                        </div>
                      </div>
                    )
                  )}
                {/* <div className="text-start flex flex-row gap-8 berg-components-attractions__desc">
                  {description}{' '}
                  <button onClick={handleToggleInfoPopup} type="button">
                    {t('attractions.full_desc')}
                  </button>
                </div> */}
                <div className="text-start berg-components-attractions__desc">
                  {description} <br />
                  <a onClick={handleToggleInfoPopup} className="underline ">
                    {t('components:attractions.full_desc')}
                  </a>
                </div>
              </div>
            </div>
            <div className="berg-components-attractions__rules berg-components-attractions__rules--bottom">
              <Icons.CalendarToday />
              <p>
                <Trans
                  ns="components"
                  i18nKey="attractions.rules"
                  components={{
                    strong: <strong />,
                  }}
                />
              </p>
            </div>
          </div>
        </form>
      ) : (
        <form
          onSubmit={handleAddToBasket}
          className="berg-components-attractions-wrapper"
        >
          <p className="absolute z-50 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-1/2 content-center text-[25px] sm:text-[60px] p-4 uppercase bg-gray-500 text-white rounded-2xl leading-[3rem] sm:leading-[5rem]">
            {t('components:attractions.package_not_available_change_date')}
          </p>
          <div className="berg-components-attractions__picture flex flex-col opacity-50">
            <img src={coverImage?.url} alt={name} loading="lazy" />

            <div className="flex flex-col gap-4 bg-white rounded-2xl sm:pb-8">
              {/* <div
                className="berg-components-attractions__box"
                data-client-style={styleForClient}
              >
                {t('attractions.box')}
              </div> */}
              <div className="berg-components-attractions__title flex flex-row gap-1 justify-center">
                <div className="name">{name}</div>
                <button onClick={handleToggleInfoPopup} type="button">
                  <Icons.InfoBrama />
                </button>
              </div>
              <div
                className="berg-components-attractions__sum"
                aria-disabled={!quantities}
              >
                <div className="berg-components-attractions__sum--second">
                  <p>
                    {t('components:attractions.from')}{' '}
                    {getPriceDisplay(defaultPrice || 0)}
                  </p>
                  {consumerCategories?.find((typ) => typ.name == 'normalny')
                    ?.priceRecalculated === true && (
                    <div>
                      {t('components:attractions.cheap')}{' '}
                      {getPriceDisplay(conversionOfDiscountIntoCash)}
                    </div>
                  )}
                </div>
                {consumerCategories?.find((typ) => typ.name == 'normalny')
                  ?.priceRecalculated === true && (
                  <div className="berg-components-attractions__sum--first">
                    {t('components:attractions.price_before_recalculated')}{' '}
                    <p>{getPriceDisplay(minPriceBeforeRecalculation || 0)}</p>
                  </div>
                )}

                <div className="berg-components-attractions__sum--third">
                  {t('components:attractions.price_previous_days')}{' '}
                  {getPriceDisplay(defaultPrice || 0)}
                </div>
                {availabilityCount !== 0 && unlimited === false && (
                  <div className="berg-components-attractions__sum--first text-[14px] mt-[5px]">
                    {t('components:attractions.number_of_available_packages')}:{' '}
                    {availabilityCount}
                  </div>
                )}
                {availabilityCount === 0 && unlimited === false && (
                  <div className="berg-components-attractions__sum--first text-[14px] mt-[5px]">
                    {t('components:attractions.package_not_available')}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="berg-components-attractions__content  opacity-50">
            <div className="flex flex-row gap-8 absolute top-[-1.5rem] sm:top-[-2rem]">
              <div
                className={`bg-white rounded-lg w-full p-1 sm:p-2 text-black border-solid border-[#b0cb1f] border-2 text-[14px] sm:text-[20px] font-semibold text-nowrap`}
              >
                {productComponents?.length}{' '}
                {t('components:attractions.attractions')}
              </div>
              {discountPercentage > 0 && (
                <div
                  className={`bg-[#b0cb1f] rounded-lg w-full p-1 sm:p-2 text- text-[14px] sm:text-[20px] font-semibold text-nowrap`}
                >
                  {t('components:attractions.saving')} -{discountPercentage}%
                </div>
              )}
            </div>
            <div className="berg-components-attractions__choices">
              {consumerCategories.map((category) => (
                <div
                  key={category.id}
                  className="flex flex-row items-center justify-between rectangle"
                >
                  <div className="flex flex-row gap-2">
                    <span>{category.name}</span>
                    <button
                      onClick={() => handleCategoryInfoClick(category.id)}
                      type="button"
                    >
                      {category.description && <Icons.InfoBrama />}
                    </button>
                    {getPriceDisplay(category?.price?.grossAmount)}
                  </div>

                  <Components.NumberCounter
                    counter={quantities[category?.id] || 0}
                    onChange={(count: number) =>
                      handleQuantityChange(category?.id, count)
                    }
                    maxCounter={99}
                    disabled
                  />
                </div>
              ))}
              {productComponents &&
                productComponents.map(
                  (product: IProductComponents, productIndex: number) =>
                    product.productAdditionalSettings.length > 0 ? (
                      <div
                        key={product.id}
                        className="flex flex-col sm:flex-row items-center w-full gap-8"
                        style={{
                          zIndex: productComponents?.length - productIndex,
                        }}
                      >
                        <div className="text-start">
                          {product.productAdditionalSettings.map((setting) => (
                            <div
                              key={String(setting.id ?? setting.name)}
                              className="mb-3"
                            >
                              <span className="block mb-1">{setting.name}</span>

                              {setting.inputType === 'date' ? (
                                <div className="berg-components-date-dropdown">
                                  <DatePickerDropdown
                                    name={`AttractionsDate_${productIndex}_${
                                      setting.id ?? setting.name
                                    }`}
                                    value={setting.value ?? null}
                                    onChange={(isoYmd) =>
                                      handleAdditionalSettingChange(
                                        product.id,
                                        setting.id ?? setting.name,
                                        isoYmd
                                      )
                                    }
                                    minDate={minDateISO}
                                    maxDate={maxDateISO}
                                    placeholder={
                                      t(
                                        'components:attractions.choose'
                                      ) as string
                                    }
                                  />
                                </div>
                              ) : (
                                <Components.Dropdown
                                  options={
                                    productDropdowns.find(
                                      (d) => d.productId === product.id
                                    )?.dropdownOptions || []
                                  }
                                  name={`AttractionsTime_${productIndex}_${
                                    setting.id ?? setting.name
                                  }`}
                                  onChange={(val: string) =>
                                    handleAdditionalSettingChange(
                                      product.id,
                                      setting.id ?? setting.name,
                                      val
                                    )
                                  }
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null
                )}
            </div>
            <div
              className="berg-components-attractions__submit flex flex-col  gap-4 justify-center"
              data-client-style={styleForClient}
            >
              <div className="flex flex-row justify-center gap-2 lg:items-center">
                <strong>{t('components:attractions.total')}</strong>{' '}
                <p>{getPriceDisplay(totalPrice())}</p>
              </div>
              {Number(totalPrice()) > 0 && quantitiesSum > 0 && hours ? (
                <button>
                  {t('components:attractions.submit', { count: quantitiesSum })}
                </button>
              ) : (
                <button disabled>
                  {t('components:attractions.submit_zero')}
                </button>
              )}
            </div>

            <div className="berg-components-attractions__info">
              <div className="text-start flex flex-col gap-3 berg-components-attractions__desc">
                <span> {t('components:attractions.package')}</span>
                {description}
              </div>

              <div className="flex flex-col items-start gap-4 berg-components-attractions__hour">
                {productComponents &&
                  productComponents.map(
                    (product: IProductComponents, productIndex: number) => (
                      <div
                        key={product.id}
                        className="flex flex-row items-center gap-4"
                        style={{
                          zIndex: productComponents?.length - productIndex,
                        }}
                      >
                        <div className="flex flex-col gap-1 items-start">
                          {product.attractionName}
                          {product.productAdditionalSettings.length > 0 && (
                            <span>
                              {' '}
                              {t('components:attractions.select_packages_time')}
                            </span>
                          )}
                        </div>
                        {product.productAdditionalSettings.length > 0 && (
                          <div>
                            <Components.Dropdown
                              name={`AttractionsTime_${productIndex}`}
                              onChange={(value: string) =>
                                handleCategoryChange(
                                  value,
                                  product?.id,
                                  product.name
                                )
                              }
                            />
                          </div>
                        )}
                      </div>
                    )
                  )}
              </div>
            </div>
            <div className="berg-components-attractions__rules berg-components-attractions__rules--bottom">
              <Icons.CalendarToday />
              <p>
                <Trans
                  ns="components"
                  i18nKey="attractions.rules"
                  components={{
                    strong: <strong />,
                  }}
                />
              </p>
            </div>
          </div>
        </form>
      )}

      {showSmallPopup && <Components.SmallPopup />}
    </>
  );
};

export default Attractions;
